import React, { useState, useEffect } from 'react';
import { Form, Checkbox, message, Col, Row } from 'antd';
import LoadingButton from '../../../components/LoadingButton';
import { getPermissionGroups } from '../../../utils/permissionGroup';
import { client2 } from '../../../operationalRequest';
import _ from 'lodash';

type EditPermissionComponentProps = {
  open: boolean
  userId: string;
  role: string;
  onSubmit: (values: any) => Promise<void>;
  loading: boolean;
};

const EditPermissionComponents: React.FC<EditPermissionComponentProps> = ({ open, userId, role, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const [checkedPermissions, setCheckedPermissions] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState<Record<string, string[]>>({});
  const [disableCheckboxes, setDisableCheckboxes] = useState(false);

  const fetchPermissions = async () => {
    try {
      const response = await client2.get(`api/user/permission/${userId}`);
      const data = response?.data?.data;

      const allPermissions = getPermissionGroups(role);
      const flatPermissions = Object.values(allPermissions).flat();
      const isSuperadmin = role === 'super-admin';
      const isTenant = role === 'tenant';

      const selectedPermissions = (isSuperadmin || isTenant) ? flatPermissions : data || [];

      setCheckedPermissions(selectedPermissions);
      setPermissionGroups(allPermissions);
      setDisableCheckboxes(isSuperadmin || isTenant);
      setSelectAll(selectedPermissions.length === flatPermissions.length);
      setIndeterminate(
        selectedPermissions.length > 0 && selectedPermissions.length < flatPermissions.length
      );

      form.setFieldsValue({ permissions: selectedPermissions });
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch user permissions');
    }
  };

  const onChange = (checkedList: string[]) => {
    setCheckedPermissions(checkedList);
    form.setFieldsValue({ permissions: checkedList });
  };

  const onCheckAllChange = (e: any) => {
    const allPermissions = Object.values(permissionGroups).flat();
    const checked = e.target.checked;
    const newChecked = checked ? allPermissions : [];
    setCheckedPermissions(newChecked);
    form.setFieldsValue({ permissions: newChecked });
  };

  useEffect(() => {
    if (open) {
      fetchPermissions();
    }
  }, [userId, open]);

  useEffect(() => {
    if (open) {
      const allPermissions = Object.values(permissionGroups).flat();
      setSelectAll(checkedPermissions.length === allPermissions.length);
      setIndeterminate(
        checkedPermissions.length > 0 && checkedPermissions.length < allPermissions.length
      );
    }
  }, [checkedPermissions, permissionGroups, userId, open]);


  return (
    <Form
      key={userId + String(open)}
      form={form}
      name={`edit-permission-${userId}`}
      layout="vertical"
      onFinish={onSubmit}
      autoComplete="off"
    >
      <Form.Item label="Permissions">
        <Checkbox
          indeterminate={indeterminate}
          onChange={onCheckAllChange}
          checked={selectAll}
          disabled={disableCheckboxes}
        >
          Check All
        </Checkbox>
      </Form.Item>

      <Form.Item
        name="permissions"
        rules={[{ required: true, message: 'Please select at least one permission!' }]}
      >
        <Checkbox.Group
          key={Object.keys(permissionGroups).join('-')} // forces re-render when permissionGroups change
          onChange={onChange}
          disabled={disableCheckboxes}
        >
          <Row gutter={[16, 16]}>
            {Object.entries(permissionGroups).map(([group, permissions]) => (
              <Col span={8} key={group}>
                <div>
                  <p className="font-semibold mb-2 text-gray-900">{group}</p>
                  {permissions.map((permission) => (
                    <Col span={24} key={permission}>
                      <Checkbox value={permission} disabled={disableCheckboxes}>
                        {_.startCase(permission.replace('destroy', 'delete'))}
                      </Checkbox>
                    </Col>
                  ))}
                </div>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Form.Item>


      <Form.Item>
        <LoadingButton loading={loading} title="Update" />
      </Form.Item>
    </Form>
  );
};

export default EditPermissionComponents;
